import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Download, Upload, FolderPlus, Trash2, Edit3, X, Check, Search,
  FileText, ExternalLink, Loader2, Eye, EyeOff, ArrowLeft, Filter, Tag,
} from "lucide-react";
import { adminApi } from "../api";

const CDN_BASE = "https://doremon.ihavethepower.net/assets/uploads/";
const TIPOS_CLIENTE = [
  { id: "CS", label: "Consultor" },
  { id: "AF", label: "Afiliado" },
  { id: "PR", label: "Embaixador" },
  { id: "CT", label: "Trainee" },
  { id: "IP", label: "International Partner" },
  { id: "CF", label: "Cliente Final" },
];

function fmtBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminDownloads() {
  const [tipos, setTipos] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tipo_id: "", tipo_cliente: "CS", activo: "S", q: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showTipos, setShowTipos] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([adminApi.downloadsTipos(), adminApi.downloadsList(filters)]);
      setTipos(t.data || []);
      setDocs(l.data || []);
    } catch (e) { toast.error(e.message || "Erro ao carregar"); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const applyFilters = () => reload();

  const handleToggleActivo = async (doc) => {
    try {
      await adminApi.downloadUpdate(doc.id, { activo: doc.activo === "S" ? "N" : "S" });
      toast.success(doc.activo === "S" ? "Documento desactivado" : "Documento activado");
      reload();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminar (soft) o documento «${doc.descricao}»?\n\nO ficheiro continua no CDN e pode ser reactivado depois em Tinf_DocumentosDownload.`)) return;
    try {
      await adminApi.downloadDelete(doc.id);
      toast.success("Documento eliminado");
      reload();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    (docs || []).forEach((d) => {
      const k = d.tipo_descricao || "(sem categoria)";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    });
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [docs]);

  return (
    <div data-testid="admin-downloads-page" className="max-w-6xl">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin" className="text-xs text-[#978D78] hover:text-[#0F1829] flex items-center gap-1"><ArrowLeft size={12} /> Dashboard</Link>
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Documentos de Download</h1>
          <p className="text-sm text-[#4B5563]">Gestão dos ficheiros disponíveis em <code className="text-xs bg-[#F5F2EB] px-1.5 py-0.5 rounded">/my/downloads</code></p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowTipos(true)} data-testid="btn-manage-tipos"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB]">
            <Tag size={14} /> Categorias
          </button>
          <button onClick={() => setUploadOpen(true)} data-testid="btn-upload-doc"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#0F1829] text-white rounded hover:bg-black">
            <Upload size={14} /> Adicionar Documento
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-[#E6E1D6] rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#978D78] block mb-1">Pesquisar</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#978D78]" />
            <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={e => e.key === "Enter" && applyFilters()}
              data-testid="filter-q" placeholder="Título, ficheiro…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#978D78] block mb-1">Categoria</label>
          <select value={filters.tipo_id} onChange={e => setFilters({ ...filters, tipo_id: e.target.value })}
            data-testid="filter-tipo" className="px-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
            <option value="">Todas</option>
            {tipos.map(t => <option key={t.id} value={t.id}>{t.descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#978D78] block mb-1">Tipo Cliente</label>
          <select value={filters.tipo_cliente} onChange={e => setFilters({ ...filters, tipo_cliente: e.target.value })}
            data-testid="filter-tipo-cliente" className="px-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
            <option value="">Todos</option>
            {TIPOS_CLIENTE.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#978D78] block mb-1">Estado</label>
          <select value={filters.activo} onChange={e => setFilters({ ...filters, activo: e.target.value })}
            data-testid="filter-activo" className="px-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
            <option value="S">Activos</option>
            <option value="N">Inactivos</option>
            <option value="">Todos</option>
          </select>
        </div>
        <button onClick={applyFilters} data-testid="btn-apply-filters"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#0F1829] text-white rounded hover:bg-black">
          <Filter size={13} /> Aplicar
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-16"><Loader2 size={24} className="mx-auto animate-spin text-[#978D78]" /></div>
      ) : docs.length === 0 ? (
        <div className="text-center py-16 text-[#978D78] text-sm">Nenhum documento encontrado com estes filtros.</div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([cat, list]) => (
            <section key={cat}>
              <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#978D78] mb-2">
                {cat}
                <span className="ml-2 text-[#0F1829]">({list.length})</span>
              </h2>
              <div className="bg-white border border-[#E6E1D6] rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-[#F5F2EB] text-[10px] uppercase tracking-[0.12em] text-[#978D78]">
                    <tr>
                      <th className="text-left px-3 py-2 w-16">ID</th>
                      <th className="text-left px-3 py-2">Título</th>
                      <th className="text-left px-3 py-2">Ficheiro</th>
                      <th className="text-left px-3 py-2 w-24">Tamanho</th>
                      <th className="text-left px-3 py-2 w-20">Cliente</th>
                      <th className="text-left px-3 py-2 w-20">Estado</th>
                      <th className="text-right px-3 py-2 w-40">Acções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((d, i) => (
                      <tr key={d.id} data-testid={`row-doc-${d.id}`}
                        className={`border-t border-[#F3F0E7] hover:bg-[#FAF8F2] ${d.activo === "N" ? "opacity-60" : ""}`}>
                        <td className="px-3 py-2 font-mono text-xs text-[#978D78]">{d.id}</td>
                        <td className="px-3 py-2 font-medium text-[#0F1829]">{d.descricao}</td>
                        <td className="px-3 py-2">
                          {d.nome_fisico ? (
                            <a href={`${CDN_BASE}${d.nome_fisico}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-[#0F1829] hover:underline"
                              data-testid={`link-doc-${d.id}`}>
                              <FileText size={12} />
                              <span className="max-w-[240px] truncate">{d.nome_fisico}</span>
                              <ExternalLink size={10} className="opacity-60" />
                            </a>
                          ) : <span className="text-xs text-red-600">(sem ficheiro)</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#4B5563] font-mono">{fmtBytes(d.tamanho_bytes)}</td>
                        <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-[#F5F2EB] text-[#0F1829] font-mono">{d.tipo_cliente}</span></td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${d.activo === "S" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {d.activo === "S" ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => setEditingDoc(d)} data-testid={`btn-edit-${d.id}`} title="Editar"
                              className="w-7 h-7 rounded hover:bg-[#F5F2EB] text-[#4B5563] flex items-center justify-center"><Edit3 size={13} /></button>
                            <button onClick={() => handleToggleActivo(d)} data-testid={`btn-toggle-${d.id}`}
                              title={d.activo === "S" ? "Desactivar" : "Activar"}
                              className="w-7 h-7 rounded hover:bg-[#F5F2EB] text-[#4B5563] flex items-center justify-center">
                              {d.activo === "S" ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                            <button onClick={() => handleDelete(d)} data-testid={`btn-delete-${d.id}`} title="Eliminar (soft)"
                              className="w-7 h-7 rounded hover:bg-red-50 text-red-600 flex items-center justify-center"><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {uploadOpen && <UploadDialog tipos={tipos} onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); reload(); }} />}
      {editingDoc && <EditDialog doc={editingDoc} tipos={tipos} onClose={() => setEditingDoc(null)} onDone={() => { setEditingDoc(null); reload(); }} />}
      {showTipos && <TiposDialog tipos={tipos} onClose={() => setShowTipos(false)} onDone={() => { setShowTipos(false); reload(); }} />}
    </div>
  );
}

// ─── Upload Dialog ────────────────────────────────────────────────

function UploadDialog({ tipos, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [tipoId, setTipoId] = useState("");
  const [tipoCliente, setTipoCliente] = useState("CS");
  const [descricao, setDescricao] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Escolha um ficheiro"); return; }
    if (!tipoId) { toast.error("Escolha uma categoria"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_id", tipoId);
      fd.append("tipo_cliente", tipoCliente);
      fd.append("descricao", descricao);
      const r = await adminApi.downloadUpload(fd);
      toast.success(`Documento #${r.id_documentos_download} adicionado`);
      onDone();
    } catch (e) { toast.error(e.message || "Erro no upload"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="upload-dialog"
        className="bg-white rounded-lg max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E6E1D6] flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>Adicionar Documento</h3>
          <button onClick={onClose} className="w-7 h-7 rounded hover:bg-[#F5F2EB] flex items-center justify-center"><X size={14} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Ficheiro *</label>
            <input ref={fileRef} type="file" onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file"
              className="w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-[#0F1829] file:text-white file:text-xs file:cursor-pointer" />
            <p className="text-[10px] text-[#978D78] mt-1">PDF, DOC, XLS, PPT, ZIP, RAR, PNG, JPG, MP4, MP3, CSV — máx 100 MB</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Categoria *</label>
            <select value={tipoId} onChange={e => setTipoId(e.target.value)} data-testid="upload-tipo"
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
              <option value="">— seleccione —</option>
              {tipos.filter(t => t.activo === "S").map(t => <option key={t.id} value={t.id}>{t.descricao}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Tipo de Cliente destinatário *</label>
            <select value={tipoCliente} onChange={e => setTipoCliente(e.target.value)} data-testid="upload-tipo-cliente"
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
              {TIPOS_CLIENTE.map(t => <option key={t.id} value={t.id}>{t.label} ({t.id})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Título (opcional)</label>
            <input value={descricao} onChange={e => setDescricao(e.target.value)} data-testid="upload-descricao"
              placeholder={file?.name?.replace(/\.[^/.]+$/, "") || "Título mostrado ao consultor"}
              maxLength={100}
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none" />
            <p className="text-[10px] text-[#978D78] mt-1">Se não preencher, é usado o nome do ficheiro sem extensão.</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm hover:bg-[#F5F2EB] rounded">Cancelar</button>
            <button type="submit" disabled={uploading} data-testid="btn-upload-submit"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#0F1829] text-white rounded hover:bg-black disabled:opacity-60">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? "A enviar…" : "Enviar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit Dialog ──────────────────────────────────────────────────

function EditDialog({ doc, tipos, onClose, onDone }) {
  const [descricao, setDescricao] = useState(doc.descricao || "");
  const [tipoId, setTipoId] = useState(String(doc.tipo_id || ""));
  const [tipoCliente, setTipoCliente] = useState(doc.tipo_cliente || "CS");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.downloadUpdate(doc.id, {
        descricao,
        tipo_id: Number(tipoId),
        tipo_cliente: tipoCliente,
      });
      toast.success("Documento actualizado");
      onDone();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="edit-dialog"
        className="bg-white rounded-lg max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E6E1D6] flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>Editar Documento #{doc.id}</h3>
          <button onClick={onClose} className="w-7 h-7 rounded hover:bg-[#F5F2EB] flex items-center justify-center"><X size={14} /></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-3">
          <p className="text-xs text-[#978D78]">Ficheiro: <code className="text-[10px]">{doc.nome_fisico || "(sem ficheiro)"}</code></p>
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Título</label>
            <input value={descricao} onChange={e => setDescricao(e.target.value)} maxLength={100} data-testid="edit-descricao"
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Categoria</label>
            <select value={tipoId} onChange={e => setTipoId(e.target.value)} data-testid="edit-tipo"
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
              {tipos.map(t => <option key={t.id} value={t.id}>{t.descricao}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#4B5563] block mb-1">Tipo de Cliente</label>
            <select value={tipoCliente} onChange={e => setTipoCliente(e.target.value)} data-testid="edit-tipo-cliente"
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none">
              {TIPOS_CLIENTE.map(t => <option key={t.id} value={t.id}>{t.label} ({t.id})</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm hover:bg-[#F5F2EB] rounded">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="btn-edit-submit"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#0F1829] text-white rounded hover:bg-black disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Tipos (Categorias) Dialog ────────────────────────────────────

function TiposDialog({ tipos, onClose, onDone }) {
  const [novo, setNovo] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState(tipos);

  useEffect(() => { setItems(tipos); }, [tipos]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!novo.trim()) return;
    setSaving(true);
    try {
      await adminApi.tipoCreate(novo.trim());
      toast.success("Categoria criada");
      setNovo("");
      onDone();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (t) => {
    try {
      await adminApi.tipoUpdate(t.id, { activo: t.activo === "S" ? "N" : "S" });
      toast.success("Categoria actualizada");
      onDone();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const handleRename = async (t, name) => {
    if (!name.trim() || name === t.descricao) { setEditing(null); return; }
    try {
      await adminApi.tipoUpdate(t.id, { descricao: name.trim() });
      toast.success("Categoria renomeada");
      setEditing(null);
      onDone();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="tipos-dialog"
        className="bg-white rounded-lg max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E6E1D6] flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>Categorias</h3>
          <button onClick={onClose} className="w-7 h-7 rounded hover:bg-[#F5F2EB] flex items-center justify-center"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-3">
          <form onSubmit={handleCreate} className="flex gap-2">
            <input value={novo} onChange={e => setNovo(e.target.value)} data-testid="tipo-novo-nome"
              placeholder="Nome da nova categoria"
              className="flex-1 px-3 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none" />
            <button type="submit" disabled={saving} data-testid="btn-tipo-criar"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[#0F1829] text-white rounded hover:bg-black disabled:opacity-60">
              <FolderPlus size={13} /> Criar
            </button>
          </form>
          <div className="max-h-96 overflow-y-auto overflow-x-auto border border-[#E6E1D6] rounded">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#F5F2EB] text-[10px] uppercase tracking-[0.12em] text-[#978D78]">
                <tr>
                  <th className="text-left px-3 py-1.5 w-12">ID</th>
                  <th className="text-left px-3 py-1.5">Nome</th>
                  <th className="text-left px-3 py-1.5 w-16">Docs</th>
                  <th className="text-right px-3 py-1.5 w-24">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map(t => (
                  <tr key={t.id} className={`border-t border-[#F3F0E7] ${t.activo === "N" ? "opacity-60" : ""}`}>
                    <td className="px-3 py-1.5 font-mono text-xs text-[#978D78]">{t.id}</td>
                    <td className="px-3 py-1.5">
                      {editing === t.id ? (
                        <input autoFocus defaultValue={t.descricao} onBlur={e => handleRename(t, e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleRename(t, e.currentTarget.value)}
                          className="w-full px-2 py-0.5 text-sm border border-[#0F1829] rounded" />
                      ) : (
                        <button onClick={() => t.alteravel === "S" && setEditing(t.id)} data-testid={`tipo-nome-${t.id}`}
                          className={`text-left ${t.alteravel === "S" ? "hover:underline cursor-text" : "cursor-not-allowed"}`}
                          title={t.alteravel === "S" ? "Clique para renomear" : "Não alterável (Alteravel='N')"}>
                          {t.descricao}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-[#4B5563]">{t.n_docs || 0}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => handleToggle(t)} data-testid={`btn-tipo-toggle-${t.id}`}
                        className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${t.activo === "S" ? "bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700" : "bg-red-100 text-red-700 hover:bg-green-100 hover:text-green-700"}`}>
                        {t.activo === "S" ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[#978D78]">Renomear: clique no nome. Toggle activo/inactivo: clique no badge de estado.</p>
        </div>
      </div>
    </div>
  );
}
